import React, { useEffect, useMemo, useState } from "react";
import Header from "../Header";
import Sidebar from "../Sidebar";
import { useSelector } from "react-redux";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { getExportItem } from "../../connections/getExportItem";
import { getJobRequestDetails } from "../../connections/getJobRequestDetails";
import { getJobListing } from "../../connections/getJobListing";
import { deleteJobRequest } from "../../connections/deleteJobRequest";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const JobStatusExportTable = ({ token, refreshKey }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchJobs();
    }
  }, [token, refreshKey]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await getJobListing(token, "invoice_items_with_purchase");
      const jobRequests = res?.payload?.job_requests || [];
      setJobs(jobRequests);
    } catch (error) {
      console.error("Fetch jobs error:", error);
      toast.error("Failed to fetch job listings");
    } finally {
      setLoading(false);
    }
  };

  const downloadFromUrl = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    if (filename) a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (job) => {
    try {
      setDeletingJobId(job.id);
      const res = await deleteJobRequest(token, job.id);
      if (res) {
        toast.success("Job request deleted successfully");
        fetchJobs();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete job request");
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    try {
      setDownloadingJobId(job.id);

      const detailsRes = await getJobRequestDetails(token, job.id);
      const attachment = detailsRes?.payload?.attachment;

      const url = attachment?.url;
      const filename = attachment?.filename;

      if (!url) {
        toast.error("Attachment URL not found");
        return;
      }

      downloadFromUrl(url, filename);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <div className="card border-0 shadow-sm mt-4">
      <div className="card-body p-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div>
            <h5 className="mb-1 fw-semibold">Job Status</h5>
            <p className="text-muted mb-0 small">
              Check progress and download the file when status is done.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={fetchJobs}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Refreshing...
              </>
            ) : (
              "Refresh"
            )}
          </button>
        </div>

        {loading ? (
          <div className="py-4 text-muted small">Loading job data...</div>
        ) : jobs?.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Request Type</th>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Start Date & Time</th>
                  <th>End Date & Time</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td>{job.request_type?.trim() || "-"}</td>
                    <td>{job.job_id || "-"}</td>
                    <td>
                      <span
                        className={`badge ${
                          job.status === "done"
                            ? "bg-success-subtle text-success"
                            : job.status === "processing"
                              ? "bg-warning-subtle text-warning"
                              : "bg-secondary-subtle text-secondary"
                        }`}
                      >
                        {job.status || "-"}
                      </span>
                    </td>
                    <td>{job.start_date || "-"}</td>
                    <td>{job.end_date || "-"}</td>
                    <td className="text-center">
                      <div className="d-flex gap-2 justify-content-center">
                        {job.status === "done" ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => handleDownload(job)}
                            disabled={downloadingJobId === job.id}
                          >
                            {downloadingJobId === job.id ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Downloading...
                              </>
                            ) : (
                              "Download"
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled
                          >
                            Not Ready
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(job)}
                          disabled={deletingJobId === job.id}
                        >
                          {deletingJobId === job.id ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Deleting...
                            </>
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-4 text-muted small">No job records found.</div>
        )}
      </div>
    </div>
  );
};

JobStatusExportTable.propTypes = {
  token: PropTypes.string,
  refreshKey: PropTypes.number,
};

const SalesExport = () => {
  const {
    user: { user },
  } = useSelector((store) => store);

  const defaultStart = useMemo(
    () => dayjs("2024-01-01").format("YYYY-MM-DD"),
    [],
  );
  const defaultEnd = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleExport = async () => {
    try {
      if (dayjs(endDate).isBefore(dayjs(startDate), "day")) {
        toast.error("End date cannot be earlier than Start date.");
        return;
      }

      setLoading(true);

      const res = await getExportItem(
        user?.token,
        "download_request",
        endDate,

        startDate,
      );
      if (res?.job_id) {
        toast.success(
          `Request sent successfully. Job ID: ${res?.job_id || "-"}`,
        );
        setRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Sidebar activeClassName="sales-report" />

      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="mb-4">
                <h4 className="mb-1 fw-semibold">Sales Report Export</h4>
                <p className="text-muted mb-0 small">
                  Select a date range to generate the invoice items export.
                </p>
              </div>

              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-4">
                  <label className="form-label fw-medium">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    max={endDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-medium">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <button
                    type="button"
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    style={{ height: "42px" }}
                    onClick={handleExport}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <FeatherIcon
                          icon="download"
                          size={16}
                          className="me-2"
                        />
                        Export Report
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-3 small text-muted">
                Selected Range:{" "}
                <span className="fw-semibold text-dark">
                  {dayjs(startDate).format("DD MMM YYYY")}
                </span>{" "}
                to{" "}
                <span className="fw-semibold text-dark">
                  {dayjs(endDate).format("DD MMM YYYY")}
                </span>
              </div>

              <div className="alert alert-light border mt-4 mb-0 d-flex align-items-center">
                <FeatherIcon icon="info" size={16} className="me-2" />
                <span>
                  After exporting, check <b>Job Status</b> below to track
                  progress and download the file.
                </span>
              </div>
            </div>
          </div>

          <JobStatusExportTable token={user?.token} refreshKey={refreshKey} />
        </div>
      </div>
    </>
  );
};

export default SalesExport;
